import React, { useState } from 'react';
import { Mail, ArrowRight, Loader2, Send } from 'lucide-react';
import { useNewsletter, NewsletterStatus } from '../hooks/useNewsletter';
import { useLanguage } from '../context/LanguageContext';

interface NewsletterFormProps {
  variant?: 'default' | 'slider';
  className?: string;
}

export const NewsletterForm: React.FC<NewsletterFormProps> = ({
  variant = 'default',
  className = ''
}) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const { status, message, subscribe } = useNewsletter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;

    try {
      const success = await subscribe(email);
      if (success) {
        setEmail('');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
    }
  };

  const getButtonStyles = (status: NewsletterStatus) => {
    if (variant === 'slider') {
      return `w-full font-medium py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transform transition-all ${status === 'loading'
          ? 'bg-gray-400 cursor-not-allowed'
          : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
        } text-white`;
    }

    return `px-4 py-2 rounded-lg transition-colors ${status === 'loading'
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-blue-600 hover:bg-blue-700'
      } text-white`;
  };

  const getInputStyles = () => {
    if (variant === 'slider') {
      return 'w-full bg-gray-50 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700 rounded-lg py-3 pl-12 pr-4 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
    }

    return 'flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400';
  };

  const getMessageStyles = () => {
    const baseStyles = 'text-sm mt-2 ';
    if (status === 'success') {
      return baseStyles + 'text-green-600 dark:text-green-400';
    }
    if (status === 'error') {
      return baseStyles + 'text-red-600 dark:text-red-400';
    }
    return baseStyles + 'text-gray-500 dark:text-gray-400';
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div className="relative">
        {variant === 'slider' && (
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
        )}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('newsletter', 'placeholder')}
          className={getInputStyles()}
          required
          disabled={status === 'loading'}
          pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
          title={t('newsletter', 'errorEmail')}
        />
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        className={getButtonStyles(status)}
      >
        {status === 'loading' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {variant === 'slider' && <span>{t('newsletter', 'subscribing')}</span>}
          </>
        ) : (
          <>
            {variant === 'slider' ? (
              <>
                <span>{t('newsletter', 'button')}</span>
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <Send className="w-5 h-5" />
            )}
          </>
        )}
      </button>
      {message && (
        <p className={getMessageStyles()}>
          {message}
        </p>
      )}
    </form>
  );
};